/**
 * The synth's parameter objects, in the spirit of ZzFX: a sound is data, rendered to samples
 * by `renderPatch`. A patch is a handful of layers (an oscillator or a noise source, a pitch
 * slide, an envelope, a filter sweep) mixed, soft-clipped and peak-normalised.
 */

export type Wave = 'sine' | 'triangle' | 'saw' | 'square' | 'noise' | 'pink' | 'brown';

export interface FilterSpec {
  type: 'lp' | 'hp' | 'bp';
  /** Hz when the layer starts, sliding exponentially to `cutoffEnd` by the time it ends. */
  cutoff: number;
  cutoffEnd?: number;
  /** Resonance; 0.707 is flat, 4+ rings. */
  q?: number;
}

export interface Layer {
  wave: Wave;
  /** Hz at the start (ignored by the noise waves). */
  freq?: number;
  /** Hz at the end of the layer: an exponential slide. */
  freqEnd?: number;
  /** Shape of the slide: below 1 drops fast then settles (drums, blasts); above 1 lingers first. */
  sweep?: number;
  /** Square only: fraction of the cycle spent high. */
  duty?: number;
  /** Vibrato, in Hz and semitones. */
  vibHz?: number;
  vibDepth?: number;
  /** Two-operator FM for metal: modulator ratio, index, and how fast the index dies away (s). */
  fmRatio?: number;
  fmIndex?: number;
  fmDecay?: number;
  /** Seconds after the patch starts. */
  at?: number;
  /** Envelope: linear attack, hold at full, then `(1 - t) ^ curve` down to silence. */
  attack?: number;
  hold?: number;
  decay: number;
  curve?: number;
  gain: number;
  /** Amplitude modulation: buzzers, flutter, a pea whistle's trill. */
  tremHz?: number;
  tremDepth?: number;
  tremSquare?: boolean;
  filter?: FilterSpec;
  /** tanh drive on this layer alone. */
  drive?: number;
  /** Sample-and-hold rate in Hz: the SID-chip crunch. */
  crush?: number;
}

export interface Patch {
  layers: Layer[];
  /** Spread applied per seeded variant, as fractions: pitch, time, per-layer gain. */
  jitter?: { pitch?: number; time?: number; gain?: number };
  /** Feed-forward slap-back taps: a box, a canyon. */
  echo?: { time: number; gain: number; count?: number };
  /** tanh drive on the mix. */
  drive?: number;
  /** Peak after normalisation, below 1. */
  peak?: number;
}

/** Peak every rendered buffer is normalised to unless the patch says otherwise. */
export const DEFAULT_PEAK = 0.9;

/** Nothing starts or stops faster than this: no clicks (07 §5). */
export const EDGE_IN_SECONDS = 0.0005;
export const EDGE_OUT_SECONDS = 0.005;
